const VERSION_CHECK_INTERVAL_MS = 60_000;
const INITIAL_VERSION_CHECK_DELAY_MS = 5_000;

type VersionWatcherOptions = {
  loadedVersion: string;
  serviceWorkerRegistration?: Promise<ServiceWorkerRegistration | null>;
  intervalMs?: number;
  initialDelayMs?: number;
};

export function startVersionWatcher({
  loadedVersion,
  serviceWorkerRegistration = Promise.resolve(null),
  intervalMs = VERSION_CHECK_INTERVAL_MS,
  initialDelayMs = INITIAL_VERSION_CHECK_DELAY_MS
}: VersionWatcherOptions) {
  let checking = false;
  let reloading = false;
  let initialTimer: number | null = null;

  const check = async () => {
    if (checking || reloading || !navigator.onLine) return;
    checking = true;
    try {
      const response = await fetch("/api/settings", {
        cache: "no-store",
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      });
      if (!response.ok) return;
      const payload = await response.json() as { settings?: { appVersion?: string } };
      const serverVersion = String(payload.settings?.appVersion || "").trim();
      if (!serverVersion || serverVersion === loadedVersion) return;

      reloading = true;
      const registration = await serviceWorkerRegistration.catch(() => null);
      await registration?.update().catch(() => {});
      window.location.replace(versionedReloadUrl(window.location.href, serverVersion));
    } catch {
      // Offline, expired sessions, and transient deploy gaps are retried later.
    } finally {
      checking = false;
    }
  };

  const checkNow = () => {
    if (initialTimer !== null) {
      window.clearTimeout(initialTimer);
      initialTimer = null;
    }
    void check();
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") checkNow();
  };
  const onFocus = () => checkNow();
  const timer = window.setInterval(() => void check(), intervalMs);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("focus", onFocus);
  window.addEventListener("online", onFocus);
  // AppShell already fetches settings during bootstrap. Yield the second
  // version-only request so it cannot contend with the initial workspace load.
  initialTimer = window.setTimeout(checkNow, initialDelayMs);

  return () => {
    if (initialTimer !== null) window.clearTimeout(initialTimer);
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("online", onFocus);
  };
}

export function versionedReloadUrl(currentUrl: string, serverVersion: string) {
  const url = new URL(currentUrl);
  url.searchParams.set("appVersion", serverVersion);
  return url.toString();
}
